'use client'

import { useCollectionBodyHtml } from '@/app/[locale]/collection/hooks/use-collection-body-html'
import { sanitize } from '@/lib/helpers/sanitize'
import { cn } from '@/lib/utils'
import { usePathname } from 'next/navigation'
import { useMemo } from 'react'

interface CollectionSeoContentProps {
  htmlContent?: string
  initialBodyHtml?: string
  className?: string
  disableApiFetch?: boolean
  slug?: string
}

function getCollectionSlugFromPathname(pathname: string): string | undefined {
  const match = pathname.match(/^\/(?:[a-z]{2}\/)?collection\/(.+)$/)
  return match ? match[1] : undefined
}

export const AmbassadorContent = ({
  htmlContent,
  initialBodyHtml,
  className,
  disableApiFetch = false,
  slug,
}: CollectionSeoContentProps) => {
  const pathname = usePathname()

  const finalSlug = useMemo(() => {
    if (disableApiFetch) return undefined
    if (slug) return slug
    return getCollectionSlugFromPathname(pathname)
  }, [pathname, disableApiFetch, slug])

  const { data: bodyHtml } = useCollectionBodyHtml({
    slug: finalSlug,
    initialBodyHtml: disableApiFetch ? undefined : initialBodyHtml,
  })

  // Priority: API data > initialBodyHtml > htmlContent
  const displayHtml = disableApiFetch 
    ? (htmlContent || initialBodyHtml) 
    : (bodyHtml || initialBodyHtml || htmlContent)

  const finalHtml = useMemo(() => {
    if (!displayHtml) return ''
    const cleanHtml = sanitize(displayHtml)

    const quoteHtml = `
      <div class="my-4 rounded-2xl bg-gray-100 p-3 text-base flex items-start md:items-center gap-4 italic">
        <img src="https://mcdn.coolmate.me/image/2026/03/23/qoute.png" alt="quote" class="w-[41px] object-contain" />
        <div class="text-neutral-800">
          $1
        </div>
      </div>`

    const processedQuoteHtml = cleanHtml.replace(
      /<(?:blockquote|q)[^>]*>([\s\S]*?)<\/(?:blockquote|q)>/gi,
      quoteHtml
    )

    return processedQuoteHtml.replace(
      /<ul\b([^>]*)>/gi,
      (match, attributes) => {
        if (/class\s*=\s*['"]/.test(attributes)) {
          return match.replace(
            /class\s*=\s*(['"])(.*?)\1/,
            'class=$1$2 list-disc$1 pl-5'
          )
        }
        return `<ul class="list-disc pl-5"${attributes}>`
      }
    )
  }, [displayHtml])

  if (!displayHtml) return <div className='mb-10'></div>

  return (
    <div className={cn('relative', className)}>
      <div className='w-full' dangerouslySetInnerHTML={{ __html: finalHtml }} />
    </div>
  )
}
